import React, { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import { useGetLocationData } from "~/hooks/locationData";
import { selectStatus } from "~/pages/Editor/EditorReports/ui/CatalogsOfReports/config/options";
import { getQuery } from "~/pages/Editor/EditorReports/ui/CatalogsOfReports/config/query";
import { getYupSchemaForReportManagement } from "~/pages/Editor/EditorReports/ui/CatalogsOfReports/ReportManagement/lib";
import { IndicatorGroup } from "~/schemas/generated/graphql";
import { capitalizeFirstLetter } from "~/shared/lib/capitalizeFirstLetter";
import { Field } from "~/shared/ui/Field";
import { IndicatorGroupStatus } from "~/types/IndicatorGroupStatus";

const defaultValues = {
  id: "",
  nameRu: "",
  status: undefined,
};

type AddReportFormData = {
  nameRu: string;
  status?: IndicatorGroupStatus;
  id?: string;
};

interface ReportManagementProps {
  refetch: any;
  currentItem: IndicatorGroup;
  closeModal: () => void;
  formId: string;
}

type SubmitData = {
  id: string;
  nameRu: string;
  status: { value: string; label: string };
};

const ReportManagement = ({
  refetch,
  currentItem,
  closeModal,
  formId,
}: ReportManagementProps) => {
  const { openModal } = useGetLocationData();
  const { t } = useTranslation();

  const isOpenAddModal = openModal === "Add";
  const isOpenEditModal = openModal === "Edit";

  const statuses = selectStatus(t);

  const { handleSubmit, control, setValue, reset, errors } =
    useForm<AddReportFormData>({
      defaultValues,
      resolver: yupResolver(getYupSchemaForReportManagement(t)),
    });

  const { createIndicatorGroup, editIndicatorGroup } = getQuery({
    closeModal,
    refetch,
    t,
  });

  const onHandleSubmitForm = (data: SubmitData) => {
    const { nameRu, status, id } = data;
    isOpenAddModal && createIndicatorGroup({ variables: { nameRu } });
    isOpenEditModal &&
      editIndicatorGroup({
        variables: {
          id,
          nameRu,
          status: IndicatorGroupStatus[capitalizeFirstLetter(status.value)],
        },
      });
  };

  useEffect(() => {
    if (currentItem) {
      const { id, nameRu, status } = currentItem;
      setValue("id", id);
      setValue("nameRu", nameRu);
      setValue("status", { label: t(`reports_list.${status}`), value: status });
    } else {
      reset();
    }
  }, [currentItem]);

  return (
    <form id={formId} onSubmit={handleSubmit(onHandleSubmitForm)}>
      <Flex vertical gap={10}>
        {isOpenEditModal && (
          <Field
            type="input"
            name="id"
            control={control}
            disabled
            label={t("reports_list.id")}
          />
        )}
        <Field
          type="input"
          name="nameRu"
          control={control}
          label={t("reports_list.nameRu")}
          error={errors["nameRu"]?.message}
        />
        {isOpenEditModal && (
          <Field
            type="select"
            name="status"
            control={control}
            label={t("reports_list.status")}
            options={statuses}
            labelInValue
          />
        )}
      </Flex>
    </form>
  );
};

export default ReportManagement;
